import { FC, Fragment, useContext, useState } from 'react';
import styled from 'styled-components';
import {
  ButtonBase,
  Divider,
  Radio,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  useUpdateAssociate,
  AssociateSetupContext,
  Store,
  useGetRetailerStores,
} from '@vs-online-only-app/state';
import { ProfileSetupContext } from '../ProfileSetupContext';
import ProfileSetupBackButton from '../ProfileSetupBackButton';
import ProfileSetupError from '../ProfileSetupError';
import ProfileSetupContinueButton from '../ProfileSetupContinueButton';

const StoreButton = styled(ButtonBase)`
  display: grid;
  grid-template-columns: auto 1fr;
  text-align: left;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  padding: ${({ theme }) => theme.spacing(1, 0)};
`;

const StoresLoadingSkeleton: FC = () => (
  <Stack gap={1}>
    {Array.from({ length: 3 }).map((_, index) => (
      <Skeleton key={index} height={40} />
    ))}
  </Stack>
);

const SelectStore: FC = () => {
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [storeId, setStoreId] = useState<Store['id']>();

  const { goToNextStep } = useContext(ProfileSetupContext);
  const { associateSetupState, refetchSetupState } = useContext(
    AssociateSetupContext,
  );
  const updateAssociate = useUpdateAssociate();

  const { data, isLoading: isLoadingStores } = useGetRetailerStores(
    associateSetupState?.retailerId as string,
  );
  const stores = data || [];

  const handleSubmit = async () => {
    setError(false);
    setIsLoading(true);

    try {
      await updateAssociate.mutateAsync({ store: storeId });
      await refetchSetupState();
      goToNextStep();
    } catch (e) {
      console.error(e);
      setError(true);
    }

    setIsLoading(false);
  };

  return (
    <Stack gap={4} sx={{ position: 'relative' }}>
      <ProfileSetupBackButton />
      <Stack gap={1} alignItems="center">
        <Typography variant="h5">Select your store location</Typography>
        <Typography textAlign="center">
          This is your primary place of work.
        </Typography>
      </Stack>
      {error && <ProfileSetupError title="store location" />}
      {isLoadingStores ? (
        <StoresLoadingSkeleton />
      ) : (
        <Stack gap={4}>
          <Stack gap={1} sx={{ maxHeight: 400, overflow: 'auto' }}>
            {stores.length > 0 &&
              stores.map((store, index) => (
                <Fragment key={store.id}>
                  {index > 0 && <Divider />}
                  <StoreButton onClick={() => setStoreId(store.id)}>
                    <Radio checked={storeId === store.id} />
                    <Typography>{store.name}</Typography>
                  </StoreButton>
                </Fragment>
              ))}
          </Stack>
          <ProfileSetupContinueButton
            onClick={handleSubmit}
            loading={isLoading || isLoadingStores}
            disabled={!storeId}
          >
            Next
          </ProfileSetupContinueButton>
        </Stack>
      )}
    </Stack>
  );
};

export default SelectStore;
